import { Link } from 'react-router-dom';
import { formatClock } from '../api/use-cart.js';
import { useCountdown } from './use-countdown.js';
import { BusyLabel } from './busy-label.js';

/**
 * The primary button, with the hold running down on it.
 *
 * On the booking screens the clock is ON the button; everywhere else it is on
 * the strip above the tabs (cart-strip.tsx, 13 October). It starts when the first lesson
 * enters the cart, not the last, so it reads the same on every screen from that
 * moment — and nothing sits underneath it explaining what it is, because a
 * countdown next to "Go to cart" explains itself.
 *
 * No `expiresAt` means nothing is held yet and the button is just a button.
 */
export function CartCta({
  to,
  label,
  expiresAt,
  onExpired,
}: {
  to: string;
  label: string;
  expiresAt?: string | null | undefined;
  /** Called once when it reaches zero, so the screen can re-read the cart. */
  onExpired?: (() => void) | undefined;
}) {
  const left = useCountdown(expiresAt ?? null, onExpired);

  return (
    <Link className="cta" to={to}>
      <span>{label}</span>
      {left !== null ? <span className="clock">{formatClock(left)}</span> : null}
    </Link>
  );
}

/** The same button when it submits rather than navigates. */
export function CartSubmit({
  label,
  expiresAt,
  disabled,
  busy = false,
  working = '',
  onExpired,
}: {
  label: string;
  expiresAt?: string | null | undefined;
  disabled?: boolean | undefined;
  /** While the call it started runs: held, and its label says what it is doing (RE-35). */
  busy?: boolean | undefined;
  working?: string | undefined;
  onExpired?: (() => void) | undefined;
}) {
  const left = useCountdown(expiresAt ?? null, onExpired);

  return (
    <button className="cta" type="submit" disabled={disabled} aria-busy={busy}>
      <span>
        <BusyLabel busy={busy} working={working}>
          {label}
        </BusyLabel>
      </span>
      {left !== null ? <span className="clock">{formatClock(left)}</span> : null}
    </button>
  );
}
